import { Fragment, useState, useCallback, useMemo, useRef } from "react";
import {
  Stack,
  Button,
  Table,
  Text,
  Box,
  Group,
  Modal,
  Select,
  Divider,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import type { UseFormReturnType } from "@mantine/form";
import { IconPlus } from "@tabler/icons-react";
import type {
  SpecimenMacroForAccessioning,
  BatchTemplateForAccessioning,
} from "../../services/workflows.service";
import type { GrossCodeOption } from "../grossing/GrossTemplateSelector";
import CollectionMethodForm, {
  type CollectionMethodFormValues,
} from "../admin/collection-methods/CollectionMethodForm";
import { useCreateCollectionMethod } from "../../hooks/admin/useCollectionMethods";
import { useHasPermission } from "../../hooks/useHasPermission";
import SpecimenRow from "./SpecimenRow";
import SpecimenPreviewRow from "./SpecimenPreviewRow";
import SpecimenEditModal from "./SpecimenEditModal";
import AddSubsiteModal from "./AddSubsiteModal";
import { getDefaultMaterialForNewSpecimen } from "../../utils/passThroughMaterialDefaults";
import {
  getNextSpecimenIdentifier,
  getSpecimenIdentifierErrors,
  normalizeSpecimenIdentifier,
} from "../../utils/specimenIdentifiers";
import type { SpecimenIndexMap } from "../../utils/specimenState";

interface SourceOption {
  value: string;
  label: string;
  defaultCollectionMethodId?: string;
  defaultCollectionMethodName?: string;
}

interface SpecimenListProps {
  form: UseFormReturnType<any>;
  certainties?: Record<string, number>;
  onCertaintyChange?: (field: string, certainty: number) => void;
  sources?: SourceOption[];
  subsites?: Record<string, Array<{ value: string; label: string }>>;
  collectionMethods?: Array<{ value: string; label: string }>;
  grossCodes?: GrossCodeOption[];
  unmatchedExtractions?: Record<string, string>;
  onClearUnmatched?: (field: string) => void;
  accessioningType?: string;
  isConsultation?: boolean;
  onFindingsBlur?: (index: number) => void;
  specimenMacros?: SpecimenMacroForAccessioning[];
  batchTemplates?: BatchTemplateForAccessioning[];
  assignedPrefix?: string;
  onSpecimensChange?: (
    specimens: any[],
    oldIndexByNewIndex?: SpecimenIndexMap
  ) => void;
  /**
   * True once the case has left Draft. Specimens already saved then carry physical
   * lineage (printed cassettes, embedded blocks, cut slides), so they can no longer
   * be removed or re-lettered — only unsaved additions stay removable.
   */
  lockPersistedSpecimens?: boolean;
  allowGrossingIdentifierEdits?: boolean;
  onSpecimenIdentifierChange?: (index: number, value: string) => void;
}

const COLUMN_COUNT = 8;

export default function SpecimenList({
  form,
  certainties = {},
  onCertaintyChange,
  sources = [],
  subsites = {},
  collectionMethods = [
    { value: "Biopsy", label: "Biopsy" },
    { value: "Excision", label: "Excision" },
    { value: "Shave", label: "Shave" },
    { value: "Punch", label: "Punch" },
    { value: "Curettage", label: "Curettage" },
    { value: "Polypectomy", label: "Polypectomy" },
  ],
  grossCodes = [],
  unmatchedExtractions = {},
  onClearUnmatched,
  accessioningType,
  isConsultation,
  onFindingsBlur,
  specimenMacros = [],
  batchTemplates = [],
  assignedPrefix,
  onSpecimensChange,
  lockPersistedSpecimens = false,
  allowGrossingIdentifierEdits = false,
  onSpecimenIdentifierChange,
}: SpecimenListProps) {
  const canCreateSubsite = useHasPermission("source_subsites.create");
  const canManageReferenceData = useHasPermission("reference_data.manage");
  const specimens = useMemo(
    () => form.values.case?.specimens || [],
    [form.values.case?.specimens]
  );
  const macroInputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const identifierErrors = useMemo(
    () => getSpecimenIdentifierErrors(specimens),
    [specimens]
  );

  const replaceSpecimens = useCallback(
    (nextSpecimens: any[], oldIndexByNewIndex?: SpecimenIndexMap) => {
      if (onSpecimensChange) {
        onSpecimensChange(nextSpecimens, oldIndexByNewIndex);
        return;
      }
      form.setFieldValue("case.specimens", nextSpecimens);
    },
    [form, onSpecimensChange]
  );

  const focusNextEmptyMacro = useCallback(
    (currentIndex: number) => {
      const specs = form.values.case?.specimens || [];
      for (let i = currentIndex + 1; i < specs.length; i += 1) {
        if (!specs[i].macroCode) {
          setTimeout(() => macroInputRefs.current[i]?.focus(), 0);
          return;
        }
      }
    },
    [form.values.case?.specimens]
  );

  const applyBatchTemplate = useCallback(
    (template: BatchTemplateForAccessioning) => {
      const newSpecimens = [];
      for (let i = 0; i < template.specimenCount; i += 1) {
        newSpecimens.push({
          number: String.fromCharCode(65 + i),
          findings: "",
          sourceId: "",
          subsiteId: null,
          collectionMethod: "",
          material: getDefaultMaterialForNewSpecimen(
            assignedPrefix,
            template.defaultMaterial ||
              (accessioningType === "global"
                ? "formalin_jar"
                : accessioningType === "consultation"
                ? "blocks_and_slides"
                : "")
          ),
          comments: "",
          extractedStainCodes: "",
          extractedGrossCodePrefix: "",
          extractedBlockIdentifiers: "",
          extractedDimensions: "",
          gross: "",
          grossCodeId: null,
          grossTemplate: "",
          blockCount: 1,
          macroCode: "",
        });
      }
      replaceSpecimens(newSpecimens);
      setTimeout(() => {
        macroInputRefs.current[0]?.focus();
      }, 0);
    },
    [accessioningType, assignedPrefix, replaceSpecimens]
  );

  const [
    deleteModalOpened,
    { open: openDeleteModal, close: closeDeleteModal },
  ] = useDisclosure(false);
  const [specimenToDelete, setSpecimenToDelete] = useState<{
    index: number;
    letter: string;
  } | null>(null);

  // Per-specimen full-edit modal
  const [editIndex, setEditIndex] = useState<number | null>(null);
  const editingSpecimen = useMemo(
    () => (editIndex !== null ? specimens[editIndex] : null),
    [editIndex, specimens]
  );

  const handleOpenEdit = useCallback((index: number) => {
    setEditIndex(index);
  }, []);

  const handleEditClose = useCallback(() => {
    setEditIndex(null);
  }, []);

  // Collection Method quick-add modal
  const [
    collectionMethodModalOpened,
    { open: openCollectionMethodModal, close: closeCollectionMethodModal },
  ] = useDisclosure(false);
  const [addMethodForIndex, setAddMethodForIndex] = useState<number | null>(
    null
  );
  const createCollectionMethod = useCreateCollectionMethod();

  const handleAddCollectionMethodClick = useCallback(
    (specimenIndex: number) => {
      setAddMethodForIndex(specimenIndex);
      openCollectionMethodModal();
    },
    [openCollectionMethodModal]
  );

  const handleCollectionMethodSubmit = useCallback(
    async (values: CollectionMethodFormValues) => {
      const method = await createCollectionMethod.mutateAsync({
        name: values.name,
        description: values.description || undefined,
        isActive: values.isActive,
      });
      if (addMethodForIndex !== null) {
        form.setFieldValue(
          `case.specimens.${addMethodForIndex}.collectionMethod`,
          method.name
        );
        onCertaintyChange?.(
          `case.specimens.${addMethodForIndex}.collectionMethod`,
          100
        );
        onClearUnmatched?.(
          `case.specimens.${addMethodForIndex}.collectionMethod`
        );
      }
      closeCollectionMethodModal();
      setAddMethodForIndex(null);
    },
    [
      createCollectionMethod,
      addMethodForIndex,
      form,
      onCertaintyChange,
      onClearUnmatched,
      closeCollectionMethodModal,
    ]
  );

  const handleSmartAddCollectionMethod = useCallback(
    async (specimenIndex: number) => {
      const fieldPath = `case.specimens.${specimenIndex}.collectionMethod`;
      const extractedValue = unmatchedExtractions[fieldPath];
      if (!extractedValue) return;
      const method = await createCollectionMethod.mutateAsync({
        name: extractedValue,
      });
      form.setFieldValue(fieldPath, method.name);
      onCertaintyChange?.(fieldPath, 100);
      onClearUnmatched?.(fieldPath);
    },
    [
      unmatchedExtractions,
      createCollectionMethod,
      form,
      onCertaintyChange,
      onClearUnmatched,
    ]
  );

  const [
    subsiteModalOpened,
    { open: openSubsiteModal, close: closeSubsiteModal },
  ] = useDisclosure(false);
  const [addSubsiteForIndex, setAddSubsiteForIndex] = useState<number | null>(
    null
  );
  const [initialSubsiteName, setInitialSubsiteName] = useState("");

  const handleAddSubsite = useCallback(
    (specimenIndex: number, subsiteName: string) => {
      setAddSubsiteForIndex(specimenIndex);
      setInitialSubsiteName(subsiteName);
      openSubsiteModal();
    },
    [openSubsiteModal]
  );

  const handleCloseSubsiteModal = useCallback(() => {
    closeSubsiteModal();
    setAddSubsiteForIndex(null);
    setInitialSubsiteName("");
  }, [closeSubsiteModal]);

  const handleSubsiteCreated = useCallback(
    (subsiteId: string, sourceId: string) => {
      if (addSubsiteForIndex === null) return;
      const fieldPrefix = `case.specimens.${addSubsiteForIndex}`;
      const sourceFieldPath = `${fieldPrefix}.sourceId`;
      const subsiteFieldPath = `${fieldPrefix}.subsiteId`;
      form.setFieldValue(sourceFieldPath, sourceId);
      form.setFieldValue(subsiteFieldPath, subsiteId);
      onCertaintyChange?.(sourceFieldPath, 100);
      onCertaintyChange?.(subsiteFieldPath, 100);
      onClearUnmatched?.(subsiteFieldPath);
    },
    [addSubsiteForIndex, form, onCertaintyChange, onClearUnmatched]
  );

  const subsiteModalSources = useMemo(
    () => sources.map(({ value, label }) => ({ id: value, name: label })),
    [sources]
  );

  const preselectedSubsiteSourceId = useMemo(() => {
    if (addSubsiteForIndex === null) return undefined;
    return specimens[addSubsiteForIndex]?.sourceId || undefined;
  }, [addSubsiteForIndex, specimens]);

  const existingSubsiteNamesBySource = useMemo(
    () =>
      Object.fromEntries(
        Object.entries(subsites).map(([sourceId, options]) => [
          sourceId,
          options.map(({ label }) => label),
        ])
      ),
    [subsites]
  );

  const addSpecimen = useCallback(() => {
    const specimenCount = specimens.length as number;
    const defaultNumber = String.fromCharCode(65 + specimenCount);
    const defaultNumberTaken = specimens.some(
      (specimen: any) =>
        normalizeSpecimenIdentifier(specimen.number).toUpperCase() ===
        defaultNumber
    );
    const number =
      accessioningType === "tc_only" || defaultNumberTaken
        ? getNextSpecimenIdentifier(
            specimens.map((specimen: any) => specimen.number)
          )
        : defaultNumber;
    const newSpecimen = {
      number,
      findings: "",
      sourceId: "",
      subsiteId: null,
      collectionMethod: "",
      material: getDefaultMaterialForNewSpecimen(
        assignedPrefix,
        accessioningType === "consultation"
          ? "blocks_and_slides"
          : accessioningType === "global"
          ? "formalin_jar"
          : ""
      ),
      comments: "",
      extractedStainCodes: "",
      extractedGrossCodePrefix: "",
      extractedBlockIdentifiers: "",
      extractedDimensions: "",
      gross: "",
      grossCodeId: null,
      grossTemplate: "",
      blockCount: 1,
      macroCode: "",
    };
    replaceSpecimens(
      [...specimens, newSpecimen],
      [...specimens.map((_: any, index: number) => index), undefined]
    );
  }, [specimens, accessioningType, assignedPrefix, replaceSpecimens]);

  const removeSpecimen = useCallback(
    (index: number) => {
      const updated = specimens.filter((_: any, i: number) => i !== index);
      const oldIndexByNewIndex = specimens
        .map((_: any, oldIndex: number) => oldIndex)
        .filter((oldIndex: number) => oldIndex !== index);
      // Re-lettering rewrites the identifier of every remaining specimen, which would
      // rename rows that already exist in the database. Only safe while the case is
      // still Draft and nothing downstream references those letters.
      const nextSpecimens =
        accessioningType === "tc_only" || lockPersistedSpecimens
          ? updated
          : updated.map((spec: any, specimenIndex: number) => ({
              ...spec,
              number: String.fromCharCode(65 + specimenIndex),
            }));
      replaceSpecimens(nextSpecimens, oldIndexByNewIndex);
    },
    [accessioningType, lockPersistedSpecimens, replaceSpecimens, specimens]
  );

  const canDeleteSpecimen = useCallback(
    (specimen: any) => {
      if (lockPersistedSpecimens && specimen?.id) return false;
      return accessioningType === "tc_only" || specimens.length > 1;
    },
    [accessioningType, lockPersistedSpecimens, specimens.length]
  );

  const handleDeleteClick = useCallback(
    (index: number, letter: string) => {
      setSpecimenToDelete({ index, letter });
      openDeleteModal();
    },
    [openDeleteModal]
  );

  const confirmDelete = useCallback(() => {
    if (specimenToDelete !== null) {
      removeSpecimen(specimenToDelete.index);
      closeDeleteModal();
      setSpecimenToDelete(null);
    }
  }, [specimenToDelete, removeSpecimen, closeDeleteModal]);

  const getSourceName = useCallback(
    (sourceId: string | null | undefined) => {
      if (!sourceId) return undefined;
      return sources.find((s) => s.value === sourceId)?.label;
    },
    [sources]
  );

  const getSubsiteName = useCallback(
    (
      sourceId: string | null | undefined,
      subsiteId: string | null | undefined
    ) => {
      if (!sourceId || !subsiteId) return undefined;
      const sourceSubsites = subsites[sourceId] || [];
      return sourceSubsites.find((s) => s.value === subsiteId)?.label;
    },
    [subsites]
  );

  const handleBatchSelect = useCallback(
    (templateId: string | null) => {
      if (!templateId) return;
      const template = batchTemplates.find((t) => t.id === templateId);
      if (template) {
        applyBatchTemplate(template);
      }
    },
    [batchTemplates, applyBatchTemplate]
  );

  const batchSelectData = batchTemplates.map((t) => ({
    value: t.id,
    label: `${t.name} (${t.specimenCount} parts)`,
  }));

  const batchSelector = batchTemplates.length > 0 && (
    <Select
      placeholder="Apply batch template..."
      data={batchSelectData}
      searchable
      clearable
      value={null}
      onChange={handleBatchSelect}
      size="sm"
      style={{ width: 280 }}
    />
  );

  return (
    <Stack gap="md">
      {specimens.length === 0 ? (
        <Box
          p="xl"
          style={{
            textAlign: "center",
            border: "1px solid var(--mantine-color-gray-3)",
            borderRadius: "var(--mantine-radius-sm)",
          }}
        >
          <Text c="dimmed">
            No specimens added. Click &quot;Add Specimen&quot; to begin.
          </Text>
        </Box>
      ) : (
        <>
          <Group justify="flex-start">{batchSelector}</Group>
          <Box>
            <Table.ScrollContainer minWidth={900}>
              <Table
                withTableBorder
                withColumnBorders
                highlightOnHover
                verticalSpacing={4}
                horizontalSpacing="xs"
                styles={{
                  th: {
                    whiteSpace: "nowrap",
                    fontSize: "var(--mantine-font-size-xs)",
                    fontWeight: 600,
                    textTransform: "uppercase" as const,
                    color: "var(--mantine-color-dimmed)",
                  },
                }}
              >
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th />
                    <Table.Th>Macro</Table.Th>
                    <Table.Th>Source</Table.Th>
                    <Table.Th>Subsite</Table.Th>
                    <Table.Th>Findings</Table.Th>
                    <Table.Th>Collection Method</Table.Th>
                    <Table.Th>Material</Table.Th>
                    <Table.Th />
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {specimens.map((specimen: any, index: number) => {
                    const fieldPrefix = `case.specimens.${index}`;
                    return (
                      <Fragment
                        key={
                          specimen.uiKey ||
                          specimen.id ||
                          specimen.expectedSpecimenNumber ||
                          specimen.number
                        }
                      >
                        <SpecimenRow
                          specimen={specimen}
                          index={index}
                          form={form}
                          fieldPrefix={fieldPrefix}
                          sources={sources}
                          subsites={subsites}
                          collectionMethods={collectionMethods}
                          certainties={certainties}
                          onCertaintyChange={onCertaintyChange}
                          canDelete={canDeleteSpecimen(specimen)}
                          onDeleteClick={() =>
                            handleDeleteClick(index, specimen.number)
                          }
                          onAddCollectionMethod={() =>
                            handleAddCollectionMethodClick(index)
                          }
                          onSmartAddCollectionMethod={() =>
                            handleSmartAddCollectionMethod(index)
                          }
                          unmatchedCollectionMethod={
                            unmatchedExtractions[
                              `case.specimens.${index}.collectionMethod`
                            ]
                          }
                          unmatchedSubsite={
                            unmatchedExtractions[
                              `case.specimens.${index}.subsiteId`
                            ]
                          }
                          onAddSubsite={
                            canCreateSubsite || canManageReferenceData
                              ? handleAddSubsite
                              : undefined
                          }
                          onFindingsBlur={
                            onFindingsBlur
                              ? () => onFindingsBlur(index)
                              : undefined
                          }
                          specimenMacros={specimenMacros}
                          macroInputRef={(el) => {
                            macroInputRefs.current[index] = el;
                          }}
                          onFocusNextMacro={() => focusNextEmptyMacro(index)}
                          isConsultation={isConsultation}
                          identifierEditable={
                            allowGrossingIdentifierEdits ||
                            !lockPersistedSpecimens ||
                            accessioningType === "tc_only" ||
                            assignedPrefix?.trim().toUpperCase() === "GC"
                          }
                          identifierError={identifierErrors[index]}
                          onIdentifierChange={(value) =>
                            onSpecimenIdentifierChange?.(index, value)
                          }
                        />
                        <SpecimenPreviewRow
                          specimen={specimen}
                          colSpan={COLUMN_COUNT}
                          onEdit={() => handleOpenEdit(index)}
                        />
                      </Fragment>
                    );
                  })}
                </Table.Tbody>
              </Table>
            </Table.ScrollContainer>
          </Box>
        </>
      )}

      <Group justify="space-between">
        <Group>{batchSelector}</Group>
        <Button
          leftSection={<IconPlus size={16} />}
          size="sm"
          onClick={addSpecimen}
        >
          Add Specimen
        </Button>
      </Group>

      {/* Per-specimen full-edit modal */}
      <SpecimenEditModal
        opened={editIndex !== null}
        onClose={handleEditClose}
        index={editIndex}
        specimen={editingSpecimen}
        form={form}
        onCertaintyChange={onCertaintyChange}
        sources={sources}
        subsites={subsites}
        collectionMethods={collectionMethods}
        grossCodes={grossCodes}
        isConsultation={isConsultation}
        assignedPrefix={assignedPrefix}
        getSourceName={getSourceName}
        getSubsiteName={getSubsiteName}
      />

      {addSubsiteForIndex !== null && (
        <AddSubsiteModal
          opened={subsiteModalOpened}
          onClose={handleCloseSubsiteModal}
          sources={subsiteModalSources}
          preselectedSourceId={preselectedSubsiteSourceId}
          existingSubsiteNamesBySource={existingSubsiteNamesBySource}
          initialName={initialSubsiteName}
          onCreated={handleSubsiteCreated}
        />
      )}

      {/* Quick-Add Collection Method Modal */}
      <Modal
        opened={collectionMethodModalOpened}
        onClose={closeCollectionMethodModal}
        title="Add New Collection Method"
        size="md"
        closeOnClickOutside={false}
      >
        <CollectionMethodForm
          onSubmit={handleCollectionMethodSubmit}
          onCancel={closeCollectionMethodModal}
          isSubmitting={createCollectionMethod.isPending}
          submitLabel="Create"
        />
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal.Root
        opened={deleteModalOpened}
        onClose={closeDeleteModal}
        size="sm"
        centered
        closeOnClickOutside={false}
      >
        <Modal.Overlay />
        <Modal.Content>
          <Modal.Header>
            <Modal.Title>Delete Specimen</Modal.Title>
            <Modal.CloseButton />
          </Modal.Header>
          <Modal.Body>
            <Stack gap="md">
              <Text>
                Are you sure you want to delete{" "}
                <strong>Specimen {specimenToDelete?.letter}</strong>?
              </Text>
              <Text size="sm" c="dimmed">
                This action cannot be undone.
              </Text>
            </Stack>
          </Modal.Body>
          <Divider />
          <Group justify="space-between" p="md">
            <Button variant="subtle" onClick={closeDeleteModal}>
              Cancel
            </Button>
            <Button color="red" onClick={confirmDelete}>
              Delete
            </Button>
          </Group>
        </Modal.Content>
      </Modal.Root>
    </Stack>
  );
}
